import React, { useEffect, useRef } from 'react';

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

function loadTurnstileScript() {
  if (window.turnstile) {
    return Promise.resolve();
  }
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Turnstile script failed to load')));
      if (window.turnstile) resolve();
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Turnstile script failed to load'));
    document.head.appendChild(script);
  });
}

/**
 * Cloudflare Turnstile widget. Renders nothing when REACT_APP_TURNSTILE_SITE_KEY is unset.
 * Bump resetKey to force a fresh token (e.g. after a failed submit).
 */
export default function Turnstile({ onSuccess, onExpire, onError, resetKey = 0 }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!SITE_KEY || !containerRef.current) {
      return undefined;
    }

    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;

        if (widgetIdRef.current != null) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch (_) {
            // ignore
          }
          widgetIdRef.current = null;
          containerRef.current.innerHTML = '';
        }

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => onSuccess && onSuccess(token),
          'expired-callback': () => onExpire && onExpire(),
          'error-callback': () => onError && onError(),
        });
      })
      .catch(() => {
        if (onError) onError();
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (_) {
          // ignore
        }
        widgetIdRef.current = null;
      }
    };
    // Intentionally re-render when resetKey changes; callbacks are expected to be stable enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  if (!SITE_KEY) {
    return null;
  }

  return <div ref={containerRef} style={{ marginTop: 8 }} />;
}

export const isTurnstileConfigured = () => Boolean(SITE_KEY);
